/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of AReportingStrategyPlugin
 */

import { IReportingStrategyPlugin, ITrackedEventArgs } from 'com/didichuxing/tracker/plugin/IReportingStrategyPlugin';
import { IContext } from 'com/didichuxing/context/IContext';
import { IDeviceInfo } from 'com/didichuxing/tracker/context/IDeviceInfo';
import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { ITracker } from 'com/didichuxing/tracker/ITracker';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { IEventReportor } from 'com/didichuxing/tracker/plugin/reportor/IEventReportor';
import ReportCallbackUtil from 'com/didichuxing/tracker/plugin/utils/ReportCallbackUtil';
import BlockedEvent from 'com/didichuxing/tracker/plugin/events/BlockedEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';
// 上报通道国内域名集合
const UPLOAD_DOMAIN_LOCAL: string[] = [
    'omgup.xiaojukeji.com',
    'omgup1.xiaojukeji.com',
    'omgup2.xiaojukeji.com'
];

// 上报通道国际域名集合
const UPLOAD_DOMAIN_GLOBAL: string[] = [
    'omgup.didiglobal.com'
];

// 上报通道国际域名集合
const UPLOAD_DOMAIN_RU: string[] = [
    'omgup-ru.didiglobal.com'
];

const UA_BROWSER_REGEXPS: IUaRegExpNameItem[] = [{
        uaRegExps: [/(miniProgram)\/([\w\.]+)/i], // WeChatMiniProgram，ios的ua中无miniProgram,但有MicroMessenger（表示是微信浏览器）
        name: 'WeChatMiniProgram'
    }, {
    uaRegExps: [/(micromessenger)\/([\w\.]+)/i], // WeChat
    name: 'WeChat'
}, {
    uaRegExps: [/(alipayclient)\/([\w\.]+)/i], // Alipay
    name: 'Alipay'
}, {
    uaRegExps: [/(QQ)\/([\d\.]+)/i], // QQ, aka ShouQ
    name: 'QQ'
},
{
    uaRegExps: [/(didi\.sdk)\/([\w\.]+)/i], // didi.sdk
    name: 'didi.sdk'
}, {
    name: 'dingding',
    uaRegExps: [/dingtalk/i] // ding ding talk
}, {
    name: 'didi.passenger',
    uaRegExps: [/didi\.passenger/i] // didi乘客端
}, {
    name: 'didi.lite',
    uaRegExps: [/didi\.lite/i] // uber客户端
}, {
    name: 'zhuankuai.driver',
    uaRegExps: [
        /didigsui/i, // didi专快司机端安卓版本
        /ddudriver/i // didi专快司机端ios版本
    ] // didi专快司机端
}, {
    name: 'zhuanche.assistant.driver',
    uaRegExps: [/zhuanche\.cici/i] // 专车司机助手
}, {
    name: 'didi.galaxy',
    uaRegExps: [/didi\.galaxy/i] // 滴滴哈勃效率工具
}, {
    name: 'daijia.driver',
    uaRegExps: [/daijia\.driver/i] // 代驾司机端
}, {
    name: 'didi.driver.tw',
    uaRegExps: [/didi\.driver\.tw/i] // didi司机端台湾
}, {
    name: 'didi.driver.hk',
    uaRegExps: [/didi\.driver\.hk/i] // didi司机端台湾
}, {
    name: 'didi.global',
    uaRegExps: [/global/i] // didi国际化端
}, {
    name: 'didi.mini',
    uaRegExps: [/mini/i] // didi乘客端极简版
}, {
    name: 'didies',
    uaRegExps: [/didies/i] // 企业版APP
},
{
    name: 'hm',
    uaRegExps: [/hm\.passenger/i] // 街兔app
}, {
    name: 'weibo',
    uaRegExps: [/weibo/i] // weibo
}];

const UA_OS_REGEXPS: IUaRegExpNameItem[] = [{
    uaRegExps: [/(android)[\/\s-]?([\w\.]*)/i], // WeChat
    name: 'Android'
}, {
    uaRegExps: [
        /cfnetwork\/.+darwin/i,
        /ip[honead]{2,4}(?:.*os\s([\w]+)\slike\smac|;\sopera)/i
    ], // iOS
    name: 'iOS'
}, {
    uaRegExps: [
        /(mac\sos\sx)\s?([\w\s\.]*)/i,
        /(macintosh|mac(?=_powerpc)\s)/i
    ], // Mac OS
    name: 'Mac OS'
}, {
    uaRegExps: [
        /microsoft\s(windows)\s(vista|xp)/i, // Windows (iTunes)
        /(windows)\snt\s6\.2;\s(arm)/i // Windows RT
    ], // Windows
    name: 'Windows'
}];

export interface IUaRegExpNameItem {
    /**
     * 正则表达式
     */
    readonly uaRegExps: RegExp[];

    /**
     * 正则表达式所对应的名称
     */
    readonly name: string;
}

export interface INameVersion {
    readonly name: string;
    readonly version: string;
}

export default abstract class AReportingStrategyPlugin implements IReportingStrategyPlugin {

    /**
     * 安装状态
     */
    private installed: boolean;

    /**
     * 插件所在的宿主对象。
     */
    private tracker: ITracker;

    /**
     * BreadcrumbList 名称
     */
    public breadcrumbListName: string;

    /**
     * @constructor
     */
    constructor(tracker: ITracker) {
        this.installed = false;
        this.tracker = tracker;
        // @ts-ignore
        const AppKey: string | undefined = this.tracker.getContext().appKey || this.tracker.getContext().productName;
        this.breadcrumbListName = this.getBreadcrumbListName(AppKey || 'Other');
        Object.assign(tracker.getContext(), this.initialzedContext);
    }

    /**
     * 获取BREADCRUMBS列表名称
     * @param appKey
     */
    private getBreadcrumbListName(appKey: string): string {
        appKey = appKey.replace(/\./g, '_');
        return `____OMG_BREADCRUMBS_${appKey}__`;
    }

    private sliceBreadcrumbList(): void {
        const breadcrumbListName = (window as any)[this.breadcrumbListName] || [];
        if (breadcrumbListName.length > 10) {
            (window as any)[this.breadcrumbListName].length = 10;
        }
    }

    /**
     * 根据正则匹配UA，获取相应数据。
     * @returns {INameVersion | undefined}
     */
    private getInfo(uaRegExpNameItems: IUaRegExpNameItem[]): INameVersion | undefined {
        const ua: string = navigator.userAgent;
        for (let i: number = 0, j: number = uaRegExpNameItems.length; i < j; ++i) {
            const item: IUaRegExpNameItem = uaRegExpNameItems[i];
            let name: string = item.name;
            const uaRegExps: RegExp[] = item.uaRegExps;
            for (let idx: number = 0, len: number = uaRegExps.length; idx < len; ++idx) {
                const regExpExecArray: RegExpExecArray | null = uaRegExps[idx].exec(ua);
                if (regExpExecArray && regExpExecArray.length) {
                    let version: string = regExpExecArray[2];
                    if (name === 'iOS') {
                        version = regExpExecArray[1].replace(/_/g, '.');
                    } else if (name === 'Mac OS') {
                        version = version.replace(/_/g, '.');
                    // @ts-ignore
                    // tslint:disable-next-line:max-line-length
                    } else if (name === 'WeChat' && window.wx && wx && wx.miniProgram && wx.miniProgram.getEnv && typeof wx.miniProgram.getEnv === 'function') {
                        // ios的ua中无miniProgram,但有MicroMessenger（表示是微信浏览器）
                        // @ts-ignore
                        wx.miniProgram.getEnv((res: any) => {
                            if (res.miniprogram) {
                                name = 'WeChatMiniProgram';
                            }
                        });
                    // @ts-ignore
                    // tslint:disable-next-line:max-line-length
                    } else if (name === 'Alipay' && window.my && my && my.getEnv && typeof my.getEnv === 'function') {
                        // @ts-ignore
                        my.getEnv((res: any) => {
                            if (res.miniprogram) {
                                name = 'AlipayMiniProgram';
                            }
                        });
                    }
                    return {
                        name: name,
                        version: version
                    };
                }
            }
        }
        return undefined;
    }

    /**
     * 获取设备信息。
     * @returns {IDeviceInfo}
     */
    protected getDeviceInfo(): IDeviceInfo {
        let appName: string = 'Others';
        let appVersion: string | undefined;
        let osType: string = 'Others';
        let osVersion: string | undefined;
        const appInfo: INameVersion | undefined = this.getInfo(this.getBrowserRegExpsInUA());
        if (appInfo) {
            appName = appInfo.name;
            appVersion = appInfo.version;
        }
        const osInfo: INameVersion | undefined = this.getInfo(this.getOSRegExpsInUA());
        if (osInfo) {
            osType = osInfo.name;
            osVersion = osInfo.version;
        }
        const context: ITrackerContext = this.getContext() as ITrackerContext;
        return {
            appName: context.appName || appName,
            appVersion: context.appVersion || appVersion,
            osType: context.osType || osType,
            osVersion: context.osVersion || osVersion
        };
    }

    /**
     * 浏览器名称正则
     * @returns {IUaRegExpNameItem[]}
     */
    protected getBrowserRegExpsInUA(): IUaRegExpNameItem[] {
        return UA_BROWSER_REGEXPS;
    }

    /**
     * 操作系统名称正则
     * @returns {IUaRegExpNameItem[]}
     */
    protected getOSRegExpsInUA(): IUaRegExpNameItem[] {
        return UA_OS_REGEXPS;
    }

    /**
     * 获取上报通道域名
     * @returns {string}
     */
    protected getUploadHost(): string {
        let uploadHost: string = (this.getContext() as ITrackerContext).uploadHost || 'omgup.xiaojukeji.com';
        if (/xiaojukeji\.com/.test(uploadHost)) { // 国内域名
            if (UPLOAD_DOMAIN_LOCAL.indexOf(uploadHost) > -1) {
                uploadHost = UPLOAD_DOMAIN_LOCAL[Math.floor(Math.random() * UPLOAD_DOMAIN_LOCAL.length)];
            }
        } else if (/omgup-ru\.didiglobal.com\.com/.test(uploadHost)) { // 俄罗斯域名
            if (UPLOAD_DOMAIN_RU.indexOf(uploadHost) > -1) {
                uploadHost = UPLOAD_DOMAIN_RU[Math.floor(Math.random() * UPLOAD_DOMAIN_RU.length)];
            }
        } else if (/didiglobal\.com/.test(uploadHost)) { // 国际域名
            if (UPLOAD_DOMAIN_GLOBAL.indexOf(uploadHost) > -1) {
                uploadHost = UPLOAD_DOMAIN_GLOBAL[Math.floor(Math.random() * UPLOAD_DOMAIN_GLOBAL.length)];
            }
        }
        return uploadHost;
    }

    /**
     * 初始化设置上下文信息。
     * @returns {IContext}
     */
    protected get initialzedContext(): IContext {
        return {
            ...this.getDeviceInfo(),
            uploadHost: this.getUploadHost()
        } as ITrackerContext;
    }

    /**
     * @abstract
     * @returns {string}
     */
    public abstract getName(): string;

    /**
     * @override
     * @inheritDoc
     * @returns {IContext}
     */
    public getContext(): IContext {
        return this.tracker.getContext();
    }

    /**
     * @override
     * @inheritDoc
     * @returns {ITracker}
     */
    public getTracker(): ITracker {
        return this.tracker;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public install(): boolean {
        this.installed = true;
        return this.installed;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public hasInstalled(): boolean {
        return this.installed;
    }

    /**
     * @abstract
     */
    public abstract execute(): void;

    /**
     * @abstract
     */
    public abstract exit(): void;

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public uninstall(): boolean {
        this.exit();
        this.installed = false;
        delete this.tracker;
        return true;
    }

    /**
     * 兼容callback
     * @param {ITrackedEventArgs} eventArgs
     * @param {ITrackedEvent} event
     * @returns {ITrackedEvent}
     */
    private compatibleCallback(eventArgs: ITrackedEventArgs, event: ITrackedEvent): ITrackedEvent {
        if (eventArgs.cb) {
            eventArgs.cb(event);
        }
        return event;
    }

    /**
     * 添加TrackerEvent至Breadcrumb
     * @param event
     */
    private addTrackerEventToBreadcrumb(event: ITrackedEvent) {
        if (event.getType() === PluginUtils.EVENT_ID_CLICK) {
            if ((window as any)[this.breadcrumbListName]) {
                (window as any)[this.breadcrumbListName].push(event.serialize());
            } else {
                (window as any)[this.breadcrumbListName] = [];
                (window as any)[this.breadcrumbListName].push(event.serialize());
            }
            if ((window as any)[this.breadcrumbListName].length > 10) {
                (window as any)[this.breadcrumbListName].length = 10;
            }
        }

    }

    /**
     * 获取事件上报器。
     * @returns {IEventReportor}
     */
    protected abstract getEventReportor(event: ITrackedEvent): IEventReportor;

    /**
     * @override
     * @inheritDoc
     * @param {ITrackedEventArgs} eventArgs
     * @returns {Promise<ITrackedEvent>}
     */
    public report(eventArgs: ITrackedEventArgs): Promise<ITrackedEvent> {
        this.sliceBreadcrumbList();
        const eventBlacklist: string[] = (window as any)[ReportCallbackUtil.OMG_EVENT_BLACKLIST_NAME] || [];
        if (eventBlacklist.some((eventid: string): boolean => eventid === eventArgs.eventId)) { // 命中黑名单
            const event: ITrackedEvent = new BlockedEvent(eventArgs.eventId, eventArgs.attrs, this.getContext());
            return Promise
                .resolve(this.compatibleCallback(eventArgs, event))
                .then((finalEvent: ITrackedEvent): ITrackedEvent => {
                    this.dispatchTrackerEventUnderWindow(finalEvent);
                    return finalEvent;
                });
        } else {
            const trackedEvent: ITrackedEvent = this.generateEvent(eventArgs);
            this.dispatchTrackerEventUnderWindow(trackedEvent);
            this.addTrackerEventToBreadcrumb(trackedEvent);
            return this.getEventReportor(trackedEvent).reportEvent()
                .then(
                    (event: ITrackedEvent): ITrackedEvent => this.compatibleCallback(eventArgs, event),
                    (event: ITrackedEvent): ITrackedEvent => this.compatibleCallback(eventArgs, event)
                );
        }
    }

    /**
     * 给window派发tarcker event。
     * 派发给window的事件名为omegatrack
     * @param {ITrackedEvent} event
     */
    protected dispatchTrackerEventUnderWindow(event: ITrackedEvent): void {
        const mesageEvent: MessageEvent = document.createEvent('MessageEvent');
        mesageEvent.initMessageEvent('omegatrack', false, false, event, '', '', window);
        window.dispatchEvent(mesageEvent);
    }

    /**
     * 生成当前插件需要上报的事件对象。
     * @abstract
     * @returns {ITrackedEvent}
     */
    protected abstract generateEvent(eventArgs: ITrackedEventArgs): ITrackedEvent;
}
